import React, { useCallback, useEffect, useMemo, useState } from 'react';

function salesToken(auth) {
  try {
    if (auth === 'shift') return localStorage.getItem('shift_token') || '';
    return localStorage.getItem('admin_token') || '';
  } catch { return ''; }
}

async function salesGet(path, auth) {
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${salesToken(auth)}` },
  });
  return res.json();
}

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function money(n) {
  return `$${Math.round(Number(n) || 0).toLocaleString('zh-TW')}`;
}

function guests(n) {
  const x = Math.round((Number(n) || 0) * 10) / 10;
  return String(x).replace(/\.0$/, '');
}

function pctText(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const x = Number(n);
  const sign = x > 0 ? '+' : '';
  return `${sign}${x}%`;
}

function pctChange(cur, prev) {
  const a = Number(cur);
  const b = Number(prev);
  if (!Number.isFinite(b) || b === 0) return null;
  if (!Number.isFinite(a)) return null;
  return Math.round(((a - b) / Math.abs(b)) * 1000) / 10;
}

function zeroPair() {
  return { amount: 0, guests: 0 };
}

function sumTotals(groups, period) {
  return (groups || []).reduce((acc, g) => {
    const t = g.totals?.[period] || {};
    acc.amount += Number(t.amount) || 0;
    acc.guests += Number(t.guests) || 0;
    return acc;
  }, zeroPair());
}

function subPair(a, b) {
  return {
    amount: (Number(a?.amount) || 0) - (Number(b?.amount) || 0),
    guests: (Number(a?.guests) || 0) - (Number(b?.guests) || 0),
  };
}

function isPianoGroup(name) {
  return String(name || '').includes('鋼琴畫室');
}

function isVenueGroup(name) {
  return String(name || '').includes('包場');
}

const PERIODS = [
  { id: 'yesterday', label: '昨日' },
  { id: 'thisWeek', label: '本週' },
  { id: 'lastWeek', label: '上週' },
  { id: 'thisMonth', label: '本月' },
  { id: 'custom', label: '自訂' },
];

function GroupTable({ groups, period, open, onToggle, emptyText, loading }) {
  const grand = sumTotals(groups, period);
  return (
    <div style={{ overflow: 'auto', maxHeight: 'calc(100dvh - 220px)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
        <thead>
          <tr>
            <th style={{ ...th, position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>群組</th>
            <th style={{ ...th, textAlign: 'right', position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>營收</th>
            <th style={{ ...th, textAlign: 'right', position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>來客</th>
          </tr>
        </thead>
        <tbody>
          {(groups || []).map((g) => {
            const tot = g.totals?.[period] || zeroPair();
            const opened = open.has(g.name);
            return (
              <React.Fragment key={g.name}>
                <tr>
                  <td style={td}>
                    <button type="button" onClick={() => onToggle(g.name)} style={{
                      border: 'none', background: 'transparent', fontWeight: 800, cursor: 'pointer', padding: 0,
                    }}>
                      {opened ? '▾ ' : '▸ '}{g.name}
                    </button>
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(tot.amount)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{guests(tot.guests)}</td>
                </tr>
                {opened ? (g.units || []).map((u) => {
                  const row = u[period] || zeroPair();
                  return (
                    <tr key={`${g.name}-${u.unit}`}>
                      <td style={{ ...td, paddingLeft: 28, color: '#475569' }}>{u.unit}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{money(row.amount)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{guests(row.guests)}</td>
                    </tr>
                  );
                }) : null}
                {opened ? (
                  <tr>
                    <td style={{ ...td, paddingLeft: 28, fontWeight: 800, color: '#334155' }}>小計</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{money(tot.amount)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{guests(tot.guests)}</td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
          {(groups || []).length ? (
            <tr style={{ background: '#f8fafc' }}>
              <td style={{ ...td, fontWeight: 800 }}>合計</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{money(grand.amount)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 800 }}>{guests(grand.guests)}</td>
            </tr>
          ) : null}
          {!loading && !(groups || []).length ? (
            <tr><td style={td} colSpan={3}>{emptyText || '無資料'}</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export default function SalesRevenueView({ auth = 'admin', compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('yesterday');
  const [split, setSplit] = useState(true);
  const [open, setOpen] = useState(() => new Set());
  const [customFrom, setCustomFrom] = useState(() => addDaysYmd(todayYmd(), -13));
  const [customTo, setCustomTo] = useState(() => todayYmd());

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams();
      if (period === 'custom') {
        if (!customFrom || !customTo || customFrom > customTo) {
          setErr('請選正確日期');
          setData(null);
          setLoading(false);
          return;
        }
        qs.set('from', customFrom);
        qs.set('to', customTo);
      }
      const path = qs.toString()
        ? `/api/sales/revenue-overview?${qs}`
        : '/api/sales/revenue-overview';
      const r = await salesGet(path, auth);
      if (!r?.ok) throw new Error(r?.message || r?.error || '載入失敗');
      setData(r);
    } catch (e) {
      setErr(e.message || '載入失敗');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [auth, period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const toggle = (name) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const classified = useMemo(() => {
    const all = data?.groups || [];
    const piano = [];
    const venue = [];
    const core = [];
    for (const g of all) {
      if (isPianoGroup(g.name)) piano.push(g);
      else if (isVenueGroup(g.name)) venue.push(g);
      else core.push(g);
    }
    return { all, piano, venue, core };
  }, [data]);

  const kpis = useMemo(() => {
    const raw = data?.kpis;
    if (!raw) return null;
    if (!split) return raw;
    const drop = [...classified.piano, ...classified.venue];
    const y = subPair(raw.yesterday, sumTotals(drop, 'yesterday'));
    const tw = subPair(raw.thisWeek, sumTotals(drop, 'thisWeek'));
    const lw = subPair(raw.lastWeek, sumTotals(drop, 'lastWeek'));
    const tm = subPair(raw.thisMonth, sumTotals(drop, 'thisMonth'));
    const days = Number(raw.thisMonth?.days) || 0;
    const thisMonthAvg = days ? tm.amount / days : 0;
    const next = {
      ...raw,
      yesterday: { ...raw.yesterday, ...y },
      thisWeek: { ...raw.thisWeek, ...tw },
      lastWeek: { ...raw.lastWeek, ...lw },
      thisMonth: { ...raw.thisMonth, ...tm },
      weekAmountPct: pctChange(tw.amount, lw.amount),
      weekGuestPct: pctChange(tw.guests, lw.guests),
      thisMonthAvg: Math.round(thisMonthAvg),
      lastMonthAvg: null,
      monthAvgPct: null,
    };
    if (raw.custom) {
      const c = subPair(raw.custom, sumTotals(drop, 'custom'));
      const cDays = Number(raw.custom.days) || 0;
      next.custom = { ...raw.custom, ...c, avg: cDays ? Math.round(c.amount / cDays) : 0 };
    }
    return next;
  }, [data, split, classified]);

  const tablePeriod = period === 'custom' ? 'custom' : period;
  const venuePeriod = sumTotals(classified.venue, tablePeriod);
  const mainGroups = split ? classified.core : classified.all;
  const k = kpis;
  const customK = k?.custom;
  const card = {
    flex: '1 1 140px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: compact ? 10 : 14,
  };
  const segBtn = (on) => ({
    padding: '6px 12px', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
    background: on ? '#1e293b' : '#e2e8f0',
    color: on ? '#fff' : '#475569',
  });
  const inp = {
    padding: compact ? '6px 8px' : '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 8, fontSize: compact ? 13 : 14,
    background: '#fff',
  };

  return (
    <div style={{ padding: compact ? 8 : 0, background: compact ? '#fff' : undefined, borderRadius: compact ? 12 : 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 800, fontSize: compact ? 15 : 17 }}>營收</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setSplit(false)} style={segBtn(!split)}>全部</button>
          <button type="button" onClick={() => setSplit(true)} style={segBtn(split)}>分開看</button>
          <button type="button" onClick={load} style={{
            padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', fontWeight: 700, cursor: 'pointer',
          }}>重新整理</button>
        </div>
      </div>
      {err ? <div style={{ color: '#b91c1c', marginBottom: 8 }}>{err}</div> : null}
      {loading && !data ? <div style={{ padding: 16, color: '#888' }}>載入中…</div> : null}

      {k && period === 'custom' && customK ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div style={{ ...card, flex: '1 1 220px' }}>
            <div style={label}>{customK.from} ～ {customK.to}</div>
            <div style={num}>{money(customK.amount)}</div>
            <div style={sub}>來客 {guests(customK.guests)} · 日均 {money(customK.avg != null ? customK.avg : (customK.days ? customK.amount / customK.days : 0))}</div>
          </div>
        </div>
      ) : null}

      {k && period !== 'custom' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <div style={card}>
            <div style={label}>昨日 {k.yesterday?.date || ''}</div>
            <div style={num}>{money(k.yesterday?.amount)}</div>
            <div style={sub}>來客 {guests(k.yesterday?.guests)}</div>
          </div>
          <div style={card}>
            <div style={label}>本週</div>
            <div style={num}>{money(k.thisWeek?.amount)}</div>
            <div style={sub}>來客 {guests(k.thisWeek?.guests)} · {pctText(k.weekAmountPct)}</div>
          </div>
          <div style={card}>
            <div style={label}>上週</div>
            <div style={num}>{money(k.lastWeek?.amount)}</div>
            <div style={sub}>來客 {guests(k.lastWeek?.guests)}</div>
          </div>
          <div style={card}>
            <div style={label}>月日均</div>
            <div style={num}>{money(k.thisMonthAvg)}</div>
            <div style={sub}>
              {split ? '本月' : `上月 ${money(k.lastMonthAvg)} · ${pctText(k.monthAvgPct)}`}
            </div>
          </div>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        {PERIODS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPeriod(p.id)}
            style={segBtn(period === p.id)}
          >
            {p.label}
          </button>
        ))}
        {period === 'custom' ? (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={inp} />
            <span style={{ color: '#94a3b8' }}>～</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={inp} />
          </>
        ) : null}
      </div>

      <GroupTable
        groups={mainGroups}
        period={tablePeriod}
        open={open}
        onToggle={toggle}
        loading={loading}
      />

      {split ? (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 800, fontSize: compact ? 14 : 15, marginBottom: 8 }}>包場</div>
          <div style={{ ...card, flex: '0 1 220px', marginBottom: 10 }}>
            <div style={label}>{PERIODS.find((p) => p.id === period)?.label || ''}</div>
            <div style={num}>{money(venuePeriod.amount)}</div>
            <div style={sub}>來客 {guests(venuePeriod.guests)}</div>
          </div>
          <GroupTable
            groups={classified.venue}
            period={tablePeriod}
            open={open}
            onToggle={toggle}
            loading={loading}
            emptyText="無包場資料"
          />
        </div>
      ) : null}
    </div>
  );
}

const label = { fontSize: 12, color: '#6b7280', fontWeight: 700 };
const num = { fontSize: 20, fontWeight: 800, color: '#0f172a', marginTop: 4 };
const sub = { fontSize: 12, color: '#64748b', marginTop: 4 };
const th = {
  textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#6b7280',
  borderBottom: '2px solid #e5e7eb',
};
const td = {
  padding: '8px 10px', fontSize: 14, color: '#1f2937', borderBottom: '1px solid #f1f5f9',
};
